/**
 * Browser monitoring (Grafana Faro): JS errors, Web Vitals, page loads and
 * emulator connection events, sent to an Alloy `faro.receiver`.
 *
 * Opt-in: nothing is loaded or sent when NA_FARO_URL is empty or until the
 * user accepts the cookie notice, and declining later pauses it for the rest
 * of the session. Tracing is not enabled; propagating `traceparent` to ctrl
 * needs ctrl's CORS to allow it.
 */
import type { Faro } from "@grafana/faro-web-sdk";
import { scrubItem } from "./scrub";

export interface MonitoringConfig {
  /** Faro receiver collect URL, e.g. https://faro.dev.null.autos/collect */
  url?: string;
  /** Deployment environment label (dev, demo, …). */
  environment?: string;
  /** App version, attached to every item. */
  version?: string;
  /** Value of the "cookie-consent" storage key. */
  consent?: string | null;
}

let faro: Faro | undefined;
let initializing: Promise<void> | undefined;

/** Whether monitoring should start for this config. */
export function shouldEnableMonitoring(config: MonitoringConfig): boolean {
  return Boolean(config.url?.trim()) && config.consent === "accepted";
}

/**
 * Pauses or resumes a started instance when consent changes mid-session.
 * A no-op before Faro has started; starting it is initMonitoring's job.
 */
export function setMonitoringPaused(paused: boolean): void {
  if (paused) faro?.pause();
  else faro?.unpause();
}

/**
 * Starts Faro once. The SDK is imported lazily so it is not part of the
 * bundle's critical path and is never loaded when monitoring is off.
 */
export function initMonitoring(config: MonitoringConfig): Promise<void> {
  if (!shouldEnableMonitoring(config)) return Promise.resolve();
  if (initializing) return initializing;

  initializing = import("@grafana/faro-web-sdk")
    .then(({ initializeFaro, getWebInstrumentations }) => {
      faro = initializeFaro({
        url: config.url!.trim(),
        app: {
          name: "null-console",
          version: config.version,
          environment: config.environment || "dev",
        },
        instrumentations: getWebInstrumentations({
          // Console output can carry API responses and ids; errors are
          // captured by the errors instrumentation instead.
          captureConsole: false,
        }),
        beforeSend: scrubItem,
      });
    })
    .catch((err) => {
      // Monitoring must never break the portal.
      console.debug("[Monitoring] Faro failed to start", err);
    });
  return initializing;
}

/**
 * Records a named event (e.g. an emulator connection result). Attribute
 * values must be strings. A no-op while monitoring is off.
 */
export function monitoringEvent(
  name: string,
  attributes: Record<string, string> = {},
): void {
  faro?.api.pushEvent(name, attributes);
}

/** Test hook: forget the started instance. */
export function resetMonitoringForTests(): void {
  faro = undefined;
  initializing = undefined;
}
